import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OnboardingLayout } from './OnboardingLayout';
import { onboardingPath } from './onboardingRoutes';
import { AppLogo } from './AppLogo';
import { Chevron } from './AppsDisclosure';
import { appBenefit } from './appBenefits';

interface Suggestion {
  id: string;
  targetApp: string;
  category: string;
  field: string;
  suggestedValue: Record<string, unknown>;
  userValue: Record<string, unknown> | null;
  source: 'website' | 'external' | 'inferred';
  confidence: 'high' | 'medium' | 'low';
  status: 'pending' | 'accepted' | 'rejected' | 'modified';
}

interface EnrichmentState {
  id: string;
  sourceUrl: string | null;
  status: string;
  enrichedAt: string | null;
  errorMessage: string | null;
}

interface OnboardingState {
  step: string;
  version: number;
  workspaceType: 'personal' | 'team' | 'enterprise' | null;
  enrichmentStatus: string | null;
  isComplete: boolean;
}

const APP_LABELS: Record<string, string> = {
  brandtalk: 'BrandTalk',
  'crm': 'CRM',
  outbound: 'Outbound',
  pitchless: 'PitchLess',
  vitals: 'Vitals',
};

const CONFIDENCE_COLORS: Record<string, string> = {
  high: 'bg-green-100 text-green-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-red-100 text-red-700',
};

const SOURCE_LABELS: Record<string, string> = {
  website: 'From website',
  external: 'External source',
  inferred: 'AI inferred',
};

/**
 * ReviewSuggestions page (Step 6 of onboarding)
 *
 * Displays enrichment suggestions grouped by app.
 * Each suggestion is editable and can be accepted/rejected.
 */
export function ReviewSuggestionsPage() {
  const [onboardingState, setOnboardingState] = useState<OnboardingState | null>(null);
  const [enrichment, setEnrichment] = useState<EnrichmentState | null>(null);
  const [suggestions, setSuggestions] = useState<Record<string, Suggestion[]>>({});
  // Each suggested app's logo, as Central serves it to the app switcher.
  const [appIcons, setAppIcons] = useState<Record<string, string>>({});
  // Which app groups are open; unset → only the first group is open.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [pollingCount, setPollingCount] = useState(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const continueUrl = searchParams.get('continue') || '';

  // Load enrichment state and suggestions
  useEffect(() => {
    async function loadData() {
      try {
        const [stateRes, enrichRes, suggestionsRes] = await Promise.all([
          fetch('/api/onboarding/state', { credentials: 'include' }),
          fetch('/api/enrichment/state', { credentials: 'include' }),
          fetch('/api/enrichment/suggestions', { credentials: 'include' }),
        ]);

        const stateData = await stateRes.json();
        const enrichData = await enrichRes.json();
        const suggestionsData = await suggestionsRes.json();

        if (stateRes.ok && stateData.state) {
          setOnboardingState(stateData.state);
        }

        if (enrichRes.ok && enrichData.enrichment) {
          setEnrichment(enrichData.enrichment);
        }

        if (suggestionsRes.ok && suggestionsData.suggestions) {
          setSuggestions(suggestionsData.suggestions);
        }
        if (suggestionsRes.ok && suggestionsData.appIcons && typeof suggestionsData.appIcons === 'object') {
          setAppIcons(suggestionsData.appIcons);
        }
      } catch {
        setError('Failed to load suggestions');
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, [pollingCount]);

  // Enrichment is "in flight" while pending OR processing — suggestions may
  // still arrive, so the review step stays and the page keeps polling. This
  // mirrors the backend's canonical `reviewStepApplies` determination.
  const isProcessing =
    enrichment?.status === 'processing' || enrichment?.status === 'pending';

  // Poll for enrichment completion while it is still in flight.
  useEffect(() => {
    if (isProcessing) {
      const timer = setTimeout(() => setPollingCount((c) => c + 1), 3000);
      return () => clearTimeout(timer);
    }
  }, [isProcessing, pollingCount]);

  const hasSuggestions = Object.keys(suggestions).length > 0;

  // R4: the review step is conditional. Once enrichment has finished and
  // produced nothing to review, this page must never dead-end — it recovers
  // to pricing, where the conditional progression skips the empty review.
  const reviewEmpty = !isLoading && !isProcessing && !hasSuggestions;

  useEffect(() => {
    if (reviewEmpty) {
      // Review is a business-only step (R7: mode-specific routes).
      navigate(onboardingPath('business', 'pricing', continueUrl), { replace: true });
    }
  }, [reviewEmpty, continueUrl, navigate]);

  // Count stats
  const stats = useMemo(() => {
    let total = 0;
    let accepted = 0;
    let rejected = 0;
    for (const appSuggestions of Object.values(suggestions)) {
      for (const s of appSuggestions) {
        total++;
        if (s.status === 'accepted' || s.status === 'modified') accepted++;
        if (s.status === 'rejected') rejected++;
      }
    }
    return { total, accepted, rejected, pending: total - accepted - rejected };
  }, [suggestions]);

  const handleSuggestionAction = async (id: string, status: 'accepted' | 'rejected') => {
    try {
      const res = await fetch(`/api/enrichment/suggestions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status }),
      });

      if (res.ok) {
        // Update local state
        setSuggestions((prev) => {
          const updated = { ...prev };
          for (const app of Object.keys(updated)) {
            updated[app] = updated[app].map((s) =>
              s.id === id ? { ...s, status } : s
            );
          }
          return updated;
        });
      }
    } catch {
      // Silently fail — user can retry
    }
  };

  const handleBulkAction = async (targetApp: string, status: 'accepted' | 'rejected') => {
    try {
      const res = await fetch('/api/enrichment/suggestions/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ targetApp, status }),
      });

      if (res.ok) {
        setSuggestions((prev) => {
          const updated = { ...prev };
          if (updated[targetApp]) {
            updated[targetApp] = updated[targetApp].map((s) =>
              s.status === 'pending' ? { ...s, status } : s
            );
          }
          return updated;
        });
      }
    } catch {
      // Silently fail
    }
  };

  const handleContinue = async () => {
    setIsSaving(true);
    setError('');

    try {
      const response = await fetch('/api/onboarding/step', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ step: 'review', data: {} }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to continue');
        return;
      }

      // Review is a business-only step. A personal-typed business
      // workspace skips invites and goes straight to complete.
      const nextStep = onboardingState?.workspaceType === 'personal' ? 'complete' : 'invites';
      navigate(onboardingPath('business', nextStep, continueUrl));
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    fetch('/api/onboarding/back', { method: 'POST', credentials: 'include' }).catch(() => {});
    navigate(onboardingPath('business', 'pricing', continueUrl));
  };

  if (isLoading) {
    return (
      <OnboardingLayout currentStep="review" workspaceType={onboardingState?.workspaceType} onboardingMode="business" wide>
        <div className="rounded-3xl bg-white p-10 text-center shadow-2xl shadow-black/40 ring-1 ring-black/5">
          <div className="mx-auto h-9 w-9 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent"></div>
          <p className="mt-4 text-gray-500">Loading suggestions...</p>
        </div>
      </OnboardingLayout>
    );
  }

  // R4: nothing to review — show a brief transition while the redirect
  // effect above bounces back to pricing. Never a dead-end empty screen.
  if (reviewEmpty) {
    return (
      <OnboardingLayout currentStep="review" workspaceType={onboardingState?.workspaceType} onboardingMode="business">
        <div className="rounded-3xl bg-white p-10 text-center shadow-2xl shadow-black/40 ring-1 ring-black/5">
          <div className="mx-auto h-9 w-9 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent"></div>
          <p className="mt-4 text-gray-500">Setting up your workspace...</p>
        </div>
      </OnboardingLayout>
    );
  }

  let sourceHost = '';
  if (enrichment?.sourceUrl) {
    try {
      sourceHost = new URL(enrichment.sourceUrl).hostname.replace(/^www\./, '');
    } catch {
      sourceHost = enrichment.sourceUrl;
    }
  }

  return (
    <OnboardingLayout currentStep="review" workspaceType={onboardingState?.workspaceType} onboardingMode="business" wide>
      <div className="rounded-3xl bg-white shadow-2xl shadow-black/40 ring-1 ring-black/5">
        <div className="grid lg:grid-cols-12">
          <aside className="relative overflow-hidden rounded-t-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-900 p-4 text-white sm:p-8 lg:col-span-4 lg:rounded-l-3xl lg:rounded-tr-none">
            <div aria-hidden="true" className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-emerald-500/20 blur-3xl" />
            <div className="relative">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-300 sm:text-xs">Website insights</p>
              <div className="mt-2.5 flex items-center gap-3 sm:mt-5">
                <span className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/15 sm:h-12 sm:w-12 sm:rounded-2xl">
                  <svg className="h-5 w-5 text-emerald-300 sm:h-6 sm:w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.6}
                      d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                    />
                  </svg>
                </span>
                {sourceHost && <p className="min-w-0 break-all text-sm font-medium text-slate-200">{sourceHost}</p>}
              </div>

              {hasSuggestions ? (
                <dl className="mt-3 grid grid-cols-4 gap-2 sm:mt-6 lg:grid-cols-2 lg:gap-3">
                  {[
                    { label: 'Total', value: stats.total, tone: 'text-white' },
                    { label: 'Accepted', value: stats.accepted, tone: 'text-emerald-300' },
                    { label: 'Rejected', value: stats.rejected, tone: 'text-rose-300' },
                    { label: 'Pending', value: stats.pending, tone: 'text-amber-200' },
                  ].map((stat) => (
                    <div key={stat.label} className="rounded-xl bg-white/[0.07] px-2.5 py-2 ring-1 ring-white/10 sm:p-3 lg:rounded-2xl lg:p-4">
                      <dt className="text-[11px] text-slate-300 lg:text-xs">{stat.label}</dt>
                      <dd className={`mt-0.5 text-xl font-semibold lg:mt-1 lg:text-3xl ${stat.tone}`}>{stat.value}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="mt-3 text-sm text-slate-300 sm:mt-6">We&apos;re reading your website now.</p>
              )}

              <p className="mt-6 hidden border-t border-white/10 pt-6 text-sm text-slate-300 lg:block">
                Accept what fits your business and reject what doesn&apos;t.
              </p>
            </div>
          </aside>

          <section className="min-w-0 p-4 sm:p-8 lg:col-span-8">
            <div className="mb-4 sm:mb-6">
              <h1 className="text-xl font-semibold tracking-tight text-gray-900 sm:text-3xl">
                Review & Apply Suggestions
              </h1>
              <p className="mt-1 text-sm text-gray-500 sm:mt-1.5 sm:text-base">
                {hasSuggestions
                  ? 'Review the apps, workflows, and automations we suggest — and why — then choose what to apply'
                  : 'Analyzing your website — suggestions will appear here shortly'}
              </p>
            </div>

            {/* Processing indicator */}
            {isProcessing && (
              <div className="mb-6 flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                <div className="h-5 w-5 flex-shrink-0 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent"></div>
                <div className="text-sm">
                  <p className="font-medium text-emerald-900">Analyzing your website...</p>
                  <p className="mt-0.5 text-emerald-700">This usually takes 15-30 seconds. Results will appear automatically.</p>
                </div>
              </div>
            )}

            {/* Suggestions grouped by app */}
            {hasSuggestions && (
              <div className="space-y-4">
                {Object.entries(suggestions).map(([appId, appSuggestions], groupIndex) => {
                  // The first app starts open; the rest fold under their header.
                  const groupOpen = openGroups[appId] ?? groupIndex === 0;
                  const pendingCount = appSuggestions.filter((x) => x.status === 'pending').length;
                  // Review is business-only; each app name carries its plain benefit.
                  const appName = APP_LABELS[appId] || appId;
                  const benefit = appBenefit({ appId, displayName: appName }, 'business');
                  return (
                  <div key={appId} className="overflow-hidden rounded-2xl border border-gray-100 shadow-sm">
                    {/* App header — the name toggles the group open and closed. */}
                    <div className="flex flex-col gap-2.5 bg-gray-50/80 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-5">
                      <h2 className="min-w-0 sm:flex-1">
                        <button
                          type="button"
                          onClick={() => setOpenGroups((prev) => ({ ...prev, [appId]: !groupOpen }))}
                          aria-expanded={groupOpen}
                          aria-controls={`review-group-${appId}`}
                          data-testid={`review-group-toggle-${appId}`}
                          className="flex w-full items-center gap-3 rounded-lg text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
                        >
                          <AppLogo appId={appId} displayName={appName} iconUrl={appIcons[appId]} size="sm" />
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-2">
                              <span className="truncate font-semibold text-gray-900">{appName}</span>
                              <span className="flex-none rounded-full bg-white px-2 py-0.5 text-xs font-medium text-gray-500 ring-1 ring-gray-200">
                                {appSuggestions.length}
                              </span>
                            </span>
                            {benefit && (
                              <span
                                className="block text-xs font-normal leading-snug text-gray-500"
                                data-testid={`review-group-benefit-${appId}`}
                              >
                                {benefit}
                              </span>
                            )}
                            {!groupOpen && (
                              <span className="block text-xs font-normal text-gray-500">
                                {pendingCount > 0 ? `${pendingCount} to review` : 'All reviewed'}
                              </span>
                            )}
                          </span>
                          <Chevron expanded={groupOpen} className="text-gray-400" />
                        </button>
                      </h2>
                      <div className="flex gap-2 pl-11 sm:flex-none sm:pl-0">
                        <button
                          type="button"
                          onClick={() => handleBulkAction(appId, 'accepted')}
                          className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-emerald-700"
                        >
                          Accept All
                        </button>
                        <button
                          type="button"
                          onClick={() => handleBulkAction(appId, 'rejected')}
                          className="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 ring-1 ring-gray-200 transition-colors hover:bg-gray-100"
                        >
                          Reject All
                        </button>
                      </div>
                    </div>

                    {/* Suggestion items */}
                    <div id={`review-group-${appId}`} hidden={!groupOpen} className="divide-y divide-gray-100">
                      {appSuggestions.map((suggestion) => {
                        const displayValue =
                          (suggestion.suggestedValue as Record<string, string>).text ||
                          (suggestion.suggestedValue as Record<string, string>).name ||
                          (suggestion.suggestedValue as Record<string, string>).description ||
                          (suggestion.suggestedValue as Record<string, string>).summary ||
                          (suggestion.suggestedValue as Record<string, string>).segment ||
                          JSON.stringify(suggestion.suggestedValue);

                        const isAccepted = suggestion.status === 'accepted' || suggestion.status === 'modified';
                        const isRejected = suggestion.status === 'rejected';

                        return (
                          <div
                            key={suggestion.id}
                            className={`flex items-start gap-4 px-4 py-3.5 transition-colors sm:px-5 ${
                              isRejected ? 'opacity-50' : isAccepted ? 'bg-emerald-50/40' : ''
                            }`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="mb-1 flex flex-wrap items-center gap-2">
                                <span className="text-sm font-semibold text-gray-900">{suggestion.field}</span>
                                <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${CONFIDENCE_COLORS[suggestion.confidence]}`}>
                                  {suggestion.confidence}
                                </span>
                                <span className="text-xs text-gray-400">
                                  {SOURCE_LABELS[suggestion.source]}
                                </span>
                              </div>
                              <p className="break-words text-sm text-gray-600 sm:truncate">{displayValue}</p>
                              <span className="text-xs text-gray-400">{suggestion.category}</span>
                            </div>

                            {/* Action buttons */}
                            <div className="flex flex-shrink-0 gap-1.5">
                              <button
                                type="button"
                                onClick={() => handleSuggestionAction(suggestion.id, 'accepted')}
                                className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${
                                  isAccepted
                                    ? 'bg-emerald-600 text-white shadow-sm'
                                    : 'text-gray-400 ring-1 ring-gray-200 hover:bg-emerald-50 hover:text-emerald-600'
                                }`}
                                title="Accept"
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleSuggestionAction(suggestion.id, 'rejected')}
                                className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${
                                  isRejected
                                    ? 'bg-rose-600 text-white shadow-sm'
                                    : 'text-gray-400 ring-1 ring-gray-200 hover:bg-rose-50 hover:text-rose-600'
                                }`}
                                title="Reject"
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}

            {error && (
              <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
          </section>
        </div>

        <div className="sticky bottom-0 z-10 flex flex-row items-center justify-between gap-3 rounded-b-3xl border-t border-gray-100 bg-white px-3 py-2 sm:px-8 sm:py-4">
          <button
            type="button"
            onClick={handleBack}
            className="px-4 py-2.5 font-medium text-gray-600 transition-colors hover:text-gray-900 sm:px-6"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleContinue}
            disabled={isSaving || isProcessing}
            className="rounded-xl bg-emerald-600 px-5 py-2.5 font-semibold sm:px-7 sm:py-3 text-white shadow-lg shadow-emerald-600/25 transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSaving ? 'Continuing...' : 'Continue'}
          </button>
        </div>
      </div>
    </OnboardingLayout>
  );
}
